/* ==========================================================================
   Executive Beverage — Motion primitive
   --------------------------------------------------------------------------
   ONE shared reveal-on-scroll implementation for the whole site. Sections opt
   in with class="eb-reveal"; they never ship their own animation code.

   Deliberate constraints:
   - No dependencies. Not ScrollReveal, not GSAP, not jQuery.
   - Animates transform + opacity only (compositor-friendly, no layout).
   - Content is visible by default. This script ARMS the hidden state only
     after confirming support, so a failed/blocked load can never leave
     content invisible.
   - Honours prefers-reduced-motion, and re-checks if the user changes it.
   - Unobserves after reveal; no persistent scroll listener.

   Portable: plain ES5-compatible DOM code, no Shopify specifics.
   ========================================================================== */
(function () {
  'use strict';

  var REVEAL_SELECTOR = '.eb-reveal';
  var ARMED_ATTR = 'data-eb-reveal-armed';
  var reduceQuery =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  function prefersReduced() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  // Bail to the visible-by-default state. Safe to call at any time.
  function disarm() {
    document.documentElement.removeAttribute(ARMED_ATTR);
  }

  function revealNow(el) {
    el.classList.add('is-visible');
    el.classList.add('is-done');
  }

  function init() {
    // No IntersectionObserver (or reduced motion): leave everything visible.
    if (!('IntersectionObserver' in window) || prefersReduced()) {
      disarm();
      return;
    }

    // Arm only now that we know we can also un-hide.
    document.documentElement.setAttribute(ARMED_ATTR, '');

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (!entry.isIntersecting) continue;

          var el = entry.target;
          el.classList.add('is-visible');
          observer.unobserve(el);

          // Release will-change once the transition has finished.
          el.addEventListener(
            'transitionend',
            function handler() {
              this.classList.add('is-done');
              this.removeEventListener('transitionend', handler);
            },
            { once: true }
          );
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    var groups = {};
    var MAX_STAGGER_STEPS = 6; // 6 x 70ms = 420ms before the tail lands together

    function observe(el) {
      // Stagger siblings sharing a data-eb-reveal-group value.
      var group = el.getAttribute('data-eb-reveal-group');
      if (group) {
        groups[group] = (groups[group] || 0) + 1;
        var index = groups[group] - 1;

        /* CLAMP the delay, do not drop it.

           This previously read `if (index > 0 && index < 8)`, which gave items
           1-7 a rising delay and items 8+ NO delay at all — so on the 30-logo
           wall the first seven trickled in while the remaining twenty-two slammed
           in instantly, ahead of the cascade. A cap meant to stop late items
           waiting made them arrive first instead, which is what read as broken.

           Clamping means everything past the cap shares the maximum delay and
           lands together as one group after the cascade, which reads as
           deliberate. */
        var stagger = parseInt(
          getComputedStyle(document.documentElement)
            .getPropertyValue('--eb-reveal-stagger') || '70',
          10
        );
        var steps = index > MAX_STAGGER_STEPS ? MAX_STAGGER_STEPS : index;
        if (steps > 0) {
          el.style.setProperty('--eb-reveal-delay', steps * stagger + 'ms');
        }
      }
      observer.observe(el);
    }

    var nodes = document.querySelectorAll(REVEAL_SELECTOR);
    for (var i = 0; i < nodes.length; i++) observe(nodes[i]);

    // Anything already in view on load (above the fold) reveals immediately
    // rather than waiting for a scroll that may never happen.
    requestAnimationFrame(function () {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      for (var j = 0; j < nodes.length; j++) {
        var rect = nodes[j].getBoundingClientRect();
        if (rect.top < vh * 0.9) {
          revealNow(nodes[j]);
          observer.unobserve(nodes[j]);
        }
      }
    });

    // Theme-editor support: sections re-render without a page reload.
    document.addEventListener('shopify:section:load', function (event) {
      var fresh = event.target.querySelectorAll(REVEAL_SELECTOR);
      for (var k = 0; k < fresh.length; k++) revealNow(fresh[k]);
    });

    // If the user switches on reduced motion mid-session, stop animating.
    if (reduceQuery) {
      var onChange = function () {
        if (prefersReduced()) disarm();
      };
      if (typeof reduceQuery.addEventListener === 'function') {
        reduceQuery.addEventListener('change', onChange);
      } else if (typeof reduceQuery.addListener === 'function') {
        reduceQuery.addListener(onChange);
      }
    }
  }

  /* ========================================================================
     Count-up primitive
     ------------------------------------------------------------------------
     Replaces the old homepage counter, which loaded countUp.js from
     inorganik.github.io (a GitHub Pages demo, not a CDN), rendered a literal
     "0" until that script arrived, and reset to 0 every time you scrolled
     away because `observer.unobserve` was commented out.

     Here the FINAL number is in the HTML. This only animates from a lower
     value up to it, and only once. If this file never loads, the correct
     number is already on screen.
     ======================================================================== */
  function initCounters() {
    var nodes = document.querySelectorAll('[data-eb-count-to]');
    if (!nodes.length) return;

    // No observer support or reduced motion: the HTML value already stands.
    if (!('IntersectionObserver' in window) || prefersReduced()) return;

    var format = function (n) {
      return Math.round(n).toLocaleString('en-US');
    };

    var run = function (el) {
      var target = parseFloat(el.getAttribute('data-eb-count-to'));
      if (!isFinite(target)) return;
      var duration = parseInt(el.getAttribute('data-eb-count-duration'), 10) || 1600;

      // Counting up from 0 to 1,000,011 walks through 1, 2 ... 7 digit strings,
      // so the element's width lurches on almost every frame and the whole
      // proof bar reflows — which reads as a bad glitch, not an animation.
      //
      // Fix: start at 90% of the target so the digit count never changes, and
      // reserve the final width in ch up front. The number only ever animates
      // within a fixed footprint.
      // Starting at 90% of the target already guarantees a constant digit
      // count, so the width is stable on its own. Reserving width in `ch` as
      // well actively hurt: `ch` is the width of "0", but commas are much
      // narrower, so a 9-character "1,000,011" reserved 9ch and left a visible
      // gap before the "+" suffix.
      var startValue = Math.floor(target * 0.9);
      var finalText = format(target);

      var startTs = null;
      var step = function (ts) {
        if (startTs === null) startTs = ts;
        var p = Math.min((ts - startTs) / duration, 1);
        // easeOutExpo — fast start, gentle settle.
        var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
        if (p < 1) {
          el.textContent = format(startValue + (target - startValue) * eased);
          requestAnimationFrame(step);
        } else {
          el.textContent = finalText;
        }
      };
      requestAnimationFrame(step);
    };

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          var el = entries[i].target;
          observer.unobserve(el); // once, and stays put — the old bug
          run(el);
        }
      },
      { threshold: 0.4 }
    );

    for (var i = 0; i < nodes.length; i++) observer.observe(nodes[i]);
  }

  function boot() {
    init();
    initCounters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
